import React, { ReactNode, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useMoodleEventDetails,
  useMoodleEventOwners,
} from '../../../../hooks/useMoodleEventBooking';
import { useMoodleCourseEnrollments } from '../../../../hooks/useMoodleHooks';
import { HbrTag } from '@harbor/elements-react-standalone';
import { EventFeedbackAnalysis } from './EventFeedbackAnalysis';
import { fetchItem } from '../../../../support/queries';
import { CourseCompletion } from './CourseCompletion';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import { useSelector } from 'react-redux';
import { selectMoodleLocationsMap } from '../../../../support/slices/moodleSlice';
import { RootState } from '../../../../support/store';
import { buildCourseDetailPath } from '../../../../support/moodleDetailRoutes';

function getDomain(email: string) {
  if (!email || typeof email !== 'string') return '';
  const atIdx = email.indexOf('@');
  if (atIdx === -1) return '';
  return email.substring(atIdx + 1).toLowerCase();
}

interface Props {
  courseId?: string;
  eventId?: string;
  /** Where the back link goes. Falls back to the ?returnTo= param. */
  returnTo?: string;
}

function getBackLabel(returnTo: string) {
  if (returnTo.startsWith('/leader')) return '← Back to Leader Dashboard';
  if (returnTo.includes('/upcoming-events')) return '← Back to Upcoming Events';
  if (returnTo.includes('/previous-events')) return '← Back to Previous Events';
  if (returnTo.includes('/workshops')) return '← Back to Workshops';
  return '← Back';
}

export function EventSummaryPage(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const courseId = props.courseId ?? params.courseId;
  const eventId = props.eventId ?? params.eventId;
  const eventIdNum = eventId ? Number(eventId) : undefined;

  const returnTo =
    props.returnTo ?? searchParams.get('returnTo') ?? '/workshops/upcoming-events';
  const backLabel = getBackLabel(returnTo);

  const courseDetailPath = buildCourseDetailPath(courseId, {
    returnTo: `${window.location.pathname}${window.location.search}`,
  });

  const { data: event, isLoading: eventLoading, error: eventError } =
    useMoodleEventDetails(eventIdNum, { enabled: !!eventIdNum });

  const locations = useSelector(selectMoodleLocationsMap);
  const location = event ? locations[event.location_id] : undefined;
  const locationName = location ? location.name : '—';

  const groupId = useMemo(() => {
    if (!event || !event.custom_fields) return undefined;
    let cf = event.custom_fields;
    if (typeof cf === 'string') {
      try {
        cf = JSON.parse(cf);
      } catch (error) {
        console.warn('Error parsing custom_fields for event', error);
      }
    }
    return cf?.group_id || cf?.groupid || undefined;
  }, [event]);

  const {
    data: eventOwnersData,
    isLoading: ownersLoading,
    error: ownersError,
  } = useMoodleEventOwners(eventIdNum ?? 0, 1, 20, { enabled: !!eventIdNum });

  const {
    data: enrollments,
    isLoading: enrollmentsLoading,
    error: enrollmentsError,
  } = useMoodleCourseEnrollments(courseId, groupId, {
    enabled: !!courseId && !!groupId,
  });

  const [contents, setContents] = useState<any[]>([]);
  const [contentsLoading, setContentsLoading] = useState(true);
  const [contentsError, setContentsError] = useState<any>(null);

  useEffect(() => {
    if (!courseId) {
      setContents([]);
      setContentsError(null);
      setContentsLoading(false);
      return;
    }
    setContentsLoading(true);
    setContentsError(null);
    fetchItem(`moodle/course/${courseId}/contents/`)
      .then(setContents)
      .catch(setContentsError)
      .finally(() => setContentsLoading(false));
  }, [courseId]);

  const feedbackModules = useMemo(() => {
    if (!contents || !Array.isArray(contents)) return [];
    return contents.flatMap((section) =>
      Array.isArray(section.modules)
        ? section.modules.filter((mod) => mod.modname === 'feedback')
        : []
    );
  }, [contents]);

  const isLoading = eventLoading || enrollmentsLoading || ownersLoading || contentsLoading;
  const error = eventError || enrollmentsError || ownersError || contentsError;

  const registeredParticipantsCount = enrollments?.length || 0;
  const eventOwners = eventOwnersData?.items || [];

  const emailDomains = useMemo(() => {
    if (!enrollments) return {};
    const domains: Record<string, number> = {};
    enrollments.forEach((user: any) => {
      const domain = getDomain(user.email);
      if (!domain) return;
      domains[domain] = (domains[domain] || 0) + 1;
    });
    return domains;
  }, [enrollments]);

  const ciscoCount = emailDomains['cisco.com'] || 0;
  const otherDomains = Object.entries(emailDomains)
    .filter(([d]) => d !== 'cisco.com')
    .map(([domain, count]) => ({ domain, count }));
  const uniqueOtherDomainCount = otherDomains.length;

  // Rendered in every state so the detail page is never a dead end.
  const backNav = (
    <button
      type="button"
      onClick={() => navigate(returnTo)}
      className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left"
      style={{ marginBottom: '1em' }}
    >
      {backLabel}
    </button>
  );

  if (isLoading) {
    return (
      <div>
        {backNav}
        <StbLoader isLoading={isLoading} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {backNav}
        <p role="alert" style={{ color: 'red', margin: 'var(--hbr-spacing-lg)' }}>
          Error loading event summary: {(error as any)?.message || String(error)}
        </p>
      </div>
    );
  }

  if (!event) {
    return (
      <div>
        {backNav}
        <p style={{ margin: 'var(--hbr-spacing-lg)' }}>Event not found.</p>
      </div>
    );
  }

  return (
    <div>
      {backNav}
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '48px',
          alignItems: 'flex-start',
          marginBottom: '2.5em',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '2em' }}>{event.title}</h2>
          <div style={{ fontSize: '1.08em', color: '#444', fontWeight: 500 }}>
            Course ID:&nbsp;
            <span style={{ color: '#1D69CC' }}>{courseId}</span>
          </div>
          {courseDetailPath && (
            <button
              type="button"
              onClick={() => navigate(courseDetailPath)}
              className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left"
              style={{ marginTop: 6 }}
            >
              View course details →
            </button>
          )}
          <div style={{ margin: '16px 0 0 0', color: '#555' }}>
            <div>
              <strong>Start:</strong>{' '}
              {event.event_date ? new Date(event.event_date).toLocaleString() : 'N/A'}
            </div>
            <div>
              <strong>End:</strong>{' '}
              {event.event_end_date ? new Date(event.event_end_date).toLocaleString() : 'N/A'}
            </div>
            <div>
              <strong>Owners:</strong>{' '}
              {eventOwners.length > 0 ? (
                <div
                  style={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    marginLeft: 6,
                  }}
                >
                  {eventOwners.map((ownerItem: any) => (
                    <HbrTag key={ownerItem.user.id} size="small">
                      {ownerItem.user.name}
                    </HbrTag>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </div>
            <div>
              <strong>Location:</strong> {locationName}
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            background: '#f6fafd',
            borderRadius: 11,
            padding: '1.5em 1.5em 1.2em 1.5em',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '1.18em', marginBottom: 9 }}>Participants</div>
          <div style={{ marginBottom: 9 }}>
            <strong>Total Registered:</strong> {registeredParticipantsCount}
          </div>
          <div>
            <strong>Cisco Employees:</strong>
            <span style={{ fontWeight: 600, color: '#1D69CC', marginLeft: 7 }}>{ciscoCount}</span>
          </div>
          <div style={{ marginTop: 9 }}>
            <strong>Other Domains:</strong>
            <span style={{ fontWeight: 600, color: '#BD7202', marginLeft: 7 }}>
              {uniqueOtherDomainCount}
            </span>
            {otherDomains.length > 0 && (
              <ul
                style={{
                  margin: '6px 0 0 0',
                  paddingLeft: 18,
                  fontSize: '0.98em',
                  color: '#555',
                }}
              >
                {otherDomains.map(({ domain, count }) => (
                  <li key={domain}>
                    {[
                      domain,
                      ' ',
                      <span key="count" style={{ color: '#111', fontWeight: 500 }}>
                        ({count})
                      </span>,
                    ] as ReactNode}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="hbr-css__container-card" data-col-span="xl" data-tablet-col-span="xl">
        <CourseCompletion courseId={courseId} groupId={groupId} />
      </div>

      <div style={{ marginTop: '2.5em' }}>
        <h3>Course Feedback Activities</h3>
        {feedbackModules.length === 0 && <p>No feedback modules found in course contents.</p>}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            marginTop: 18,
          }}
        >
          {feedbackModules.map((module) => (
            <div
              key={module.id}
              className="hbr-css__container-card"
              data-col-span="xl"
              data-tablet-col-span="xl"
            >
              <div style={{ marginBottom: 12 }}>
                <strong>{module.name}</strong>
                {module.uservisible ? null : (
                  <span style={{ color: 'orange', marginLeft: 8 }}>(Not visible to user)</span>
                )}
              </div>
              <div style={{ width: '100%' }}>
                <EventFeedbackAnalysis feedbackId={module.instance} groupId={groupId} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}